import {
  Button,
  Divider,
  Form,
  FormInstance,
  Input,
  InputRef,
  Modal,
  Select,
  Space,
} from "antd";
import React, { useEffect, useRef, useState } from "react";

import { Plus } from "lucide-react";
import "quill-paste-smart";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { selectCaseIssues } from "services/caseIssues.service";
import { selectreportIssuePriorities } from "services/reportIssuePriorities.service";
interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  handleSave: () => void;
  form: FormInstance;
}

export const CreateNewReportIssueModal = ({
  isOpen,
  setIsOpen,
  handleSave,
  form,
}: Props) => {
  const { t }: { t: (key: string) => string } = useTranslation();

  const [issuesPrioritiesOptions, setIssuesPrioritiesOptions] = useState<
    SelectReportIssuePrioritiesDto[]
  >([]);
  const [caseIssuesOptions, setCaseIssuesOptions] = useState<
    SelectCaseIssueDto[]
  >([]);

  const [issueName, setIssueName] = useState("");
  const inputRef = useRef<InputRef>(null);

  const addItem = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    setCaseIssuesOptions([
      ...caseIssuesOptions,
      {
        case: issueName,
        uuid: `new-${issueName}`,
        description: "",
        active: true,
      },
    ]);
    form.setFieldValue("caseIssue", `new-${issueName}`);
    setIssueName("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const onClose = () => {
    setIsOpen(false);
    form.resetFields();
  };

  async function handleFetchCaseIssues() {
    try {
      const response = await selectCaseIssues();

      if (response) {
        setCaseIssuesOptions(response);
      }
    } catch (error) {}
  }

  async function handleFetchReportIssuesPriorities() {
    try {
      const response = await selectreportIssuePriorities();

      if (response && response.length) {
        setIssuesPrioritiesOptions(
          response
            .map((item) => {
              if (item.active)
                return {
                  ...item,
                };
              return undefined;
            })
            .filter(
              (item): item is SelectReportIssuePrioritiesDto =>
                item !== undefined
            )
        );
      }
    } catch (error) {}
  }

  useEffect(() => {
    if (isOpen) {
      handleFetchCaseIssues();
      handleFetchReportIssuesPriorities();
    }
  }, [isOpen]);

  return (
    <Modal
      onCancel={onClose}
      open={isOpen}
      destroyOnClose
      centered
      width={600}
      title={t("createNewIssue")}
      footer={null}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSave}
        className="mt-8"
      >
        <Form.Item
          name="caseIssue"
          label={<span className="font-bold">{t("caseIssue")}:</span>}
          rules={[{ required: true, message: t("thisFieldIsRequired") }]}
        >
          <Select
            id="caseIssue"
            placeholder={t("selectCaseIssue")}
            allowClear
            showSearch
            optionFilterProp="label"
            className="min-h-[40px] w-full"
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space style={{ padding: "0 8px 4px" }}>
                  <Input
                    placeholder={t("newCaseIssue")}
                    ref={inputRef}
                    value={issueName}
                    allowClear
                    className="rounded-md"
                    onChange={(e) => setIssueName(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    type="text"
                    className="flex items-center gap-2"
                    onClick={addItem}
                  >
                    <Plus size={16} />
                    {t("addItem")}
                  </Button>
                </Space>
              </>
            )}
            options={caseIssuesOptions.map((item) => ({
              label: item.case,
              value: item.uuid,
            }))}
          />
          {/* <Select
            id="caseIssue"
            placeholder="Select Case Issue"
            allowClear
            className="min-h-[40px]"
            showSearch
            optionFilterProp="label"
            options={
              caseIssuesOptions && caseIssuesOptions.length > 0
                ? caseIssuesOptions.map((caseIssue) => {
                    return {
                      value: caseIssue.uuid,
                      label: caseIssue.case,
                    };
                  })
                : []
            }
          /> */}
        </Form.Item>

        <Form.Item
          name="priority"
          label={<span className="font-bold">{t("priority")}:</span>}
          rules={[{ required: true, message: t("thisFieldIsRequired") }]}
        >
          <Select
            id="priority"
            placeholder={t("selectPriority")}
            allowClear
            className="min-h-[40px]"
            showSearch
            optionFilterProp="label"
            options={
              issuesPrioritiesOptions && issuesPrioritiesOptions.length > 0
                ? issuesPrioritiesOptions.map((priority) => {
                    return {
                      value: priority.uuid,
                      label: priority.companyPriority
                        ? priority.companyPriority
                        : priority.priority,
                    };
                  })
                : []
            }
          />
        </Form.Item>

        <Form.Item
          name="location"
          label={<span className="font-bold">{t("location")}:</span>}
        >
          <Input
            id="location"
            placeholder={t("issueLocation")}
            className="rounded-md"
            allowClear
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="subject"
          label={<span className="font-bold">{t("subject")}:</span>}
        >
          <Input
            id="subject"
            placeholder={t("issueSubject")}
            className="rounded-md"
            allowClear
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="summary"
          label={<span className="font-bold">{t("summary")}:</span>}
        >
          <Input.TextArea
            id="summary"
            placeholder={t("issueSummary")}
            className="rounded-md"
            autoSize={{ minRows: 4, maxRows: 10 }}
            allowClear
            size="large"
          />
        </Form.Item>

        <div className="w-full flex gap-2 items-center justify-end mt-5 flex-wrap">
          <Button key="cancel" size="large" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            key="ok"
            size="large"
            htmlType="submit"
            style={{
              background: "var(--primary_color)",
              color: "var(--white_A700)",
            }}
          >
            {t("createComplaintIssue")}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
